import * as React from "react";
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { SourceLinkCycleCensus, SourceLinkState, SourceLinkSummary } from "../../models/SourceLink";
import { DiscoveryCycleCensusState, DiscoveryCycleCoverage } from "../../models/Statistics/DiscoveryCycle";
import { MigrationType, MigrationTypeLabels, MigrationTypeNouns } from "../../models/Statistics/MigrationType";
import CoverageBadge from "../DiscoveryCycles/CoverageBadge";

const numberFormat = new Intl.NumberFormat();

const EXPANDED_KEY = "sourceLinks.summaryExpanded";

/** One figure. An absent value is a dash, never a zero: a cycle that did not count is not a cycle that found none. */
const Figure: React.FC<{
  label: string;
  value?: number;
  tone?: "danger" | "muted" | "default";
  onSelect?: () => void;
}> = ({ label, value, tone = "default", onSelect }) => {
  // Colour is always set here, never left to the element: a figure rendered as a link would otherwise fall back to
  // the link colour, and a zero would read as the one thing on the card to click.
  const className =
    tone === "danger" && value !== undefined && value > 0
      ? "text-lg font-semibold tabular-nums text-destructive"
      : tone === "muted" || value === 0
        ? "text-lg tabular-nums text-muted-foreground"
        : "text-lg font-semibold tabular-nums text-foreground";

  return (
    <div className="flex flex-col gap-0.5 min-w-[7rem]">
      <span className="text-xs text-muted-foreground">{label}</span>
      {value === undefined || value === null ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-lg text-muted-foreground cursor-default">&mdash;</span>
          </TooltipTrigger>
          <TooltipContent>This cycle did not report this figure.</TooltipContent>
        </Tooltip>
      ) : onSelect ? (
        // A way in to the rows rather than a figure on its own: each one is exactly what the matching state filter
        // lists, so the number and the table beneath can never disagree.
        <Button variant="link" className={`h-auto w-fit justify-start p-0 ${className}`} onClick={onSelect}>
          {numberFormat.format(value)}
        </Button>
      ) : (
        <span className={className}>{numberFormat.format(value)}</span>
      )}
    </div>
  );
};

/** A band heading. The two bands are different series, and nothing in the numbers themselves says which is which. */
const BandTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{children}</span>
);

/** A figure inside a sentence, for the collapsed summary: the number, then what it counts. */
const InlineFigure: React.FC<{
  label: string;
  value?: number;
  tone?: "danger" | "muted" | "default";
  onSelect?: () => void;
}> = ({ label, value, tone = "default", onSelect }) => {
  const numberClass =
    value === undefined || value === null
      ? "text-muted-foreground"
      : tone === "danger" && value > 0
        ? "font-semibold text-destructive"
        : tone === "muted" || value === 0
          ? "text-muted-foreground"
          : "font-semibold text-foreground";
  const number = value === undefined || value === null ? "—" : numberFormat.format(value);

  return (
    <span className="whitespace-nowrap tabular-nums">
      {onSelect && value !== undefined && value !== null ? (
        <Button variant="link" className={`h-auto p-0 text-sm ${numberClass}`} onClick={onSelect}>
          {number}
        </Button>
      ) : (
        <span className={numberClass}>{number}</span>
      )}{" "}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
};

/** Which discovery a cycle band is for, when it finished, and what may make its figures incomplete. */
const CycleHeading: React.FC<{ census: SourceLinkCycleCensus }> = ({ census }) => (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{MigrationTypeLabels[census.migrationType]}</span>
        <span>finished {census.endDate ? convertUtcToLocal(census.endDate) : "—"}</span>
        <CoverageBadge
          coverage={census.coverage}
          degraded={census.degraded}
          migrationType={census.migrationType}
          rootsUnreachable={census.rootsUnreachable}
        />
        {census.censusState === DiscoveryCycleCensusState.Withheld && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="danger">
                <AlertTriangle className="size-3" />
                No totals
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              This client uploaded the problems it found but not the totals, so there is no way to check nothing was
              lost on the way.
            </TooltipContent>
          </Tooltip>
        )}
        {census.reportsPending && census.drainComplete === undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="neutral">
                <Info className="size-3" />
                Reports pending
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              Still receiving results from this cycle. Nothing is cleared out until they all arrive.
            </TooltipContent>
          </Tooltip>
        )}
        {census.reportsPending && census.drainComplete === false && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="warning">
                <AlertTriangle className="size-3" />
                Reports incomplete
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              Some results never arrived, so nothing was cleared out. The client will resend them on the next cycle.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
);

/** What the latest finished cycle of one discovery reported. */
const CycleFigures: React.FC<{ census: SourceLinkCycleCensus }> = ({ census }) => {
  const healthy =
    census.linksFilesDiscovered === undefined || census.linksTargetEmpty === undefined
      ? undefined
      : census.linksFilesDiscovered + census.linksTargetEmpty;

  return (
    <div className="flex flex-col gap-2">
      <CycleHeading census={census} />
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Figure label="Links checked" value={census.linksScanned} />
        <Figure label="Healthy" value={healthy} tone="muted" />
        <Figure label="Nothing found" value={census.linksNothingDiscovered} tone="danger" />
        <Figure label="Partly found" value={census.linksPartiallyDiscovered} tone="danger" />
        <Figure label="Excluded" value={census.linksExcluded} tone="muted" />
        <Figure label="Duplicate" value={census.linksDuplicateTarget} tone="muted" />
        <Figure label="Roots unreachable" value={census.rootsUnreachable} tone="danger" />
      </div>
    </div>
  );
};

/** One line per cycle for the collapsed summary: how many were checked, and only the problems it found. */
const CompactCycleFigures: React.FC<{ census: SourceLinkCycleCensus }> = ({ census }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
    <CycleHeading census={census} />
    <InlineFigure label="checked" value={census.linksScanned} />
    {!!census.linksNothingDiscovered && (
      <InlineFigure label="nothing found" value={census.linksNothingDiscovered} tone="danger" />
    )}
    {!!census.linksPartiallyDiscovered && (
      <InlineFigure label="partly found" value={census.linksPartiallyDiscovered} tone="danger" />
    )}
    {!!census.rootsUnreachable && (
      <InlineFigure
        label={census.rootsUnreachable === 1 ? "root unreachable" : "roots unreachable"}
        value={census.rootsUnreachable}
        tone="danger"
      />
    )}
  </div>
);

/**
 * What clearing is doing for this type, in the one case worth a sentence each: nothing is cleared because
 * something went wrong, something is stepped around, or nothing is cleared because the client cannot say.
 * Complete and still-draining say nothing — the cycle band already carries its badge.
 */
const CoverageBanner: React.FC<{ summary: SourceLinkSummary; compact?: boolean }> = ({ summary, compact }) => {
  const nouns = MigrationTypeNouns[summary.entityType].many;

  // Collapsed, each case keeps only its headline sentence; Details carries the rest.
  if (compact && summary.coverage === DiscoveryCycleCoverage.Unbounded) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <AlertTriangle className="size-4 shrink-0" />
        <span className="font-medium">Links that disappear are not being cleared out.</span>
      </div>
    );
  }
  if (compact && summary.coverage === DiscoveryCycleCoverage.Bounded) {
    return (
      <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
        <Info className="size-4 shrink-0" />
        <span className="font-medium">Some links the last cycle could not reach are not being cleared out.</span>
      </div>
    );
  }
  if (compact && summary.coverage === DiscoveryCycleCoverage.Unproven) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Info className="size-4 shrink-0" />
        <span>Nothing is cleared out until this client reports how many {nouns} it saw.</span>
      </div>
    );
  }

  if (summary.coverage === DiscoveryCycleCoverage.Unbounded) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
        <AlertTriangle className="size-4 shrink-0 text-destructive" />
        <span>
          <span className="font-medium">Links that disappear are not being cleared out.</span> The last cycle could
          not account for everything it checked, so this list will keep growing until one can.{" "}
          {summary.lastCoveredOn
            ? `The last cycle that could started ${convertUtcToLocal(summary.lastCoveredOn)}.`
            : "No cycle of this type has been able to yet."}
        </span>
      </div>
    );
  }

  if (summary.coverage === DiscoveryCycleCoverage.Bounded) {
    const bounded = summary.latestCycles.filter((census) => census.coverage === DiscoveryCycleCoverage.Bounded);
    const roots = bounded.flatMap((census) => census.unreachableRoots);
    const unnamed = bounded.reduce(
      (total, census) => total + Math.max(0, census.rootsUnreachable - census.unreachableRoots.length),
      0
    );

    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800/60 dark:bg-amber-900/20">
        <Info className="size-4 shrink-0 text-amber-700 dark:text-amber-300" />
        <div className="flex flex-col gap-1 min-w-0">
          {roots.length > 0 || unnamed > 0 ? (
            <span>
              <span className="font-medium">
                Links beneath {roots.length + unnamed === 1 ? "a root" : `${roots.length + unnamed} roots`} the last
                cycle could not read are not being cleared out.
              </span>{" "}
              Everything else is.
            </span>
          ) : (
            <span>
              <span className="font-medium">Some {nouns} were passed over on the last cycle.</span> They are kept as
              they are, and everything else is cleared out as usual.
            </span>
          )}
          {roots.length > 0 && (
            <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
              {roots.map((root) => (
                <li key={root.pathRoot} className="flex flex-wrap gap-x-2">
                  <span className="font-mono break-all">{root.pathRoot}</span>
                  <span>
                    {numberFormat.format(root.linksSkipped)} link{root.linksSkipped === 1 ? "" : "s"} held back
                  </span>
                </li>
              ))}
            </ul>
          )}
          {unnamed > 0 && (
            <span className="text-xs text-muted-foreground">
              {unnamed === 1 ? "One more root was" : `${unnamed} more roots were`} counted but not named by the client.
            </span>
          )}
        </div>
      </div>
    );
  }

  if (summary.coverage === DiscoveryCycleCoverage.Unproven) {
    return (
      <div className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="size-4 shrink-0" />
        <span>
          This client does not report how many {nouns} it saw, so we cannot tell a deleted link from one it simply
          missed. Nothing is cleared out until it does.
        </span>
      </div>
    );
  }

  return null;
};

/**
 * The headline for the selected type, in two series that must not be read as one. The first is the registry now:
 * what is outstanding, by kind, whoever reported it and whenever. The second is what the latest finished cycle of
 * each discovery reported. The two agree only when the latest cycle re-examined every outstanding link; side by
 * side and unlabelled they would read as one series with a subtotal.
 *
 * Collapsed by default to one line per series, with only the figures that need attention, so the table starts near
 * the top of the page; Details opens the full breakdown. The choice is remembered in this browser.
 */
export const SourceLinkSummaryTiles: React.FC<{
  summary?: SourceLinkSummary;
  loading: boolean;
  entityType: MigrationType;
  onSelectState: (state: SourceLinkState) => void;
}> = ({ summary, loading, entityType, onSelectState }) => {
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setExpanded((previous) => {
      try {
        localStorage.setItem(EXPANDED_KEY, String(!previous));
      } catch {
        // Storage unavailable: the choice lasts for this page view only.
      }
      return !previous;
    });
  };

  if (loading && !summary) {
    return <div className="h-24 rounded-md border bg-muted/30 animate-pulse" />;
  }

  if (!summary) return null;

  const nouns = MigrationTypeNouns[entityType];

  const toggleButton = (
    <Button variant="ghost" size="sm" className="shrink-0 gap-1" onClick={toggle} aria-expanded={expanded}>
      {expanded ? "Hide details" : "Details"}
      {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
    </Button>
  );

  if (!expanded) {
    return (
      <Card>
        <CardContent className="flex items-start justify-between gap-3 py-3">
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <BandTitle>Outstanding now</BandTitle>
              <InlineFigure
                label="missing or unreachable"
                value={summary.outstanding.missingOrUnreachable}
                tone="danger"
                onSelect={() => onSelectState(SourceLinkState.MissingOrUnreachable)}
              />
              <InlineFigure
                label="invalid paths"
                value={summary.outstanding.invalidPaths}
                tone="muted"
                onSelect={() => onSelectState(SourceLinkState.InvalidPath)}
              />
              <InlineFigure
                label="duplicates"
                value={summary.outstanding.duplicates}
                tone="muted"
                onSelect={() => onSelectState(SourceLinkState.Duplicate)}
              />
              <InlineFigure
                label="excluded"
                value={summary.outstanding.excluded}
                tone="muted"
                onSelect={() => onSelectState(SourceLinkState.Excluded)}
              />
            </div>
            <CoverageBanner summary={summary} compact />
            <div className="flex flex-col gap-1">
              <BandTitle>Last cycle</BandTitle>
              {summary.latestCycles.length === 0 ? (
                <span className="text-sm text-muted-foreground">No completed cycle has reported totals yet.</span>
              ) : (
                summary.latestCycles.map((census) => <CompactCycleFigures key={census.cycleId} census={census} />)
              )}
            </div>
          </div>
          {toggleButton}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start justify-between gap-3">
            <BandTitle>Outstanding now</BandTitle>
            {toggleButton}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Figure
              label="Missing or unreachable"
              value={summary.outstanding.missingOrUnreachable}
              tone="danger"
              onSelect={() => onSelectState(SourceLinkState.MissingOrUnreachable)}
            />
            <Figure
              label="Invalid paths"
              value={summary.outstanding.invalidPaths}
              tone="muted"
              onSelect={() => onSelectState(SourceLinkState.InvalidPath)}
            />
            <Figure
              label="Duplicates"
              value={summary.outstanding.duplicates}
              tone="muted"
              onSelect={() => onSelectState(SourceLinkState.Duplicate)}
            />
            <Figure
              label="Excluded"
              value={summary.outstanding.excluded}
              tone="muted"
              onSelect={() => onSelectState(SourceLinkState.Excluded)}
            />
          </div>
          {/* Said in terms of what the reader sees rather than how clients report: it is what makes the Healthy
              view complete for some records and empty for others. Nothing is said when no record lists every
              link, since then the Healthy view is simply empty and there is no mix to explain. */}
          {summary.fullMapEntities > 0 && (
            <span className="text-xs text-muted-foreground">
              {numberFormat.format(summary.fullMapEntities)}{" "}
              {summary.fullMapEntities === 1 ? `${nouns.one} lists` : `${nouns.many} list`} every link, including the ones
              that work.
              The rest list only links with a problem.
            </span>
          )}
        </div>

        <CoverageBanner summary={summary} />

        <div className="flex flex-col gap-1.5">
          <BandTitle>Last cycle</BandTitle>
          {summary.latestCycles.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="size-4 shrink-0" />
              <span>No completed cycle has reported totals yet.</span>
            </div>
          ) : (
            summary.latestCycles.map((census) => <CycleFigures key={census.cycleId} census={census} />)
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SourceLinkSummaryTiles;
